import { useCallback, useEffect, useRef, useState } from "react";

import type { OcclusionShape } from "../lib/types";

type Drag =
  | { mode: "create"; startX: number; startY: number }
  | { mode: "move"; index: number; dx: number; dy: number }
  | { mode: "resize"; index: number };

const MIN_SIZE = 0.012;

/**
 * Direct manipulation editor for image occlusion regions.
 *
 * Regions are stored — and edited here — in Anki's normalised 0-1 coordinate
 * space, so what the student positions is exactly what gets written into the
 * Occlusion field. No pixel conversion happens on save, which means no
 * opportunity for the two to drift apart.
 *
 * The AI's label boxes are estimates from a vision model. This editor exists
 * because they need correcting, not as a nicety.
 */
export default function OcclusionEditor({
  imageUrl,
  shapes,
  onChange,
}: {
  imageUrl: string;
  shapes: OcclusionShape[];
  onChange: (next: OcclusionShape[]) => void;
}) {
  const stageRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [draft, setDraft] = useState<OcclusionShape | null>(null);
  const [selected, setSelected] = useState<number | null>(null);

  const point = useCallback((event: { clientX: number; clientY: number }) => {
    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return { x: 0, y: 0 };
    return {
      x: clamp((event.clientX - rect.left) / rect.width),
      y: clamp((event.clientY - rect.top) / rect.height),
    };
  }, []);

  const renumber = (list: OcclusionShape[]): OcclusionShape[] =>
    list.map((shape, i) => ({ ...shape, ordinal: i + 1 }));

  // Pointer handling lives on window so a drag that leaves the image still
  // tracks, and still ends, instead of leaving a stuck ghost rectangle.
  useEffect(() => {
    if (!drag) return;

    const onMove = (event: PointerEvent) => {
      const { x, y } = point(event);
      if (drag.mode === "create") {
        setDraft({
          shape: "rect",
          ordinal: shapes.length + 1,
          left: Math.min(drag.startX, x),
          top: Math.min(drag.startY, y),
          width: Math.abs(x - drag.startX),
          height: Math.abs(y - drag.startY),
          points: [],
          label: "",
          occlude_inactive: shapes[0]?.occlude_inactive ?? false,
        });
      } else if (drag.mode === "move") {
        const shape = shapes[drag.index];
        if (!shape) return;
        onChange(
          shapes.map((s, i) =>
            i === drag.index
              ? {
                  ...s,
                  left: clamp(x - drag.dx, 0, 1 - s.width),
                  top: clamp(y - drag.dy, 0, 1 - s.height),
                }
              : s,
          ),
        );
      } else {
        const shape = shapes[drag.index];
        if (!shape) return;
        onChange(
          shapes.map((s, i) =>
            i === drag.index
              ? {
                  ...s,
                  width: clamp(x - s.left, MIN_SIZE, 1 - s.left),
                  height: clamp(y - s.top, MIN_SIZE, 1 - s.top),
                }
              : s,
          ),
        );
      }
    };

    const onUp = () => {
      if (drag.mode === "create" && draft) {
        if (draft.width >= MIN_SIZE && draft.height >= MIN_SIZE) {
          const next = renumber([...shapes, draft]);
          onChange(next);
          setSelected(next.length - 1);
        }
      }
      setDraft(null);
      setDrag(null);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [drag, draft, shapes, onChange, point]);

  const remove = (index: number) => {
    onChange(renumber(shapes.filter((_, i) => i !== index)));
    setSelected(null);
  };

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= shapes.length) return;
    const next = [...shapes];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(renumber(next));
    setSelected(target);
  };

  const setLabel = (index: number, label: string) =>
    onChange(shapes.map((s, i) => (i === index ? { ...s, label } : s)));

  const hideAll = shapes[0]?.occlude_inactive ?? false;
  const toggleHideAll = () =>
    onChange(shapes.map((s) => ({ ...s, occlude_inactive: !hideAll })));

  const visible = draft ? [...shapes, draft] : shapes;

  return (
    <div className="stack" style={{ gap: 10 }}>
      <div
        ref={stageRef}
        className="occlusion-stage is-drawing"
        onPointerDown={(event) => {
          if (event.target !== event.currentTarget && !(event.target as HTMLElement).matches("img"))
            return;
          event.preventDefault();
          setSelected(null);
          const { x, y } = point(event);
          setDrag({ mode: "create", startX: x, startY: y });
        }}
      >
        <img src={imageUrl} alt="" draggable={false} />
        {visible.map((shape, index) => {
          const isDraft = draft != null && index === visible.length - 1 && shapes.length === index;
          return (
            <div
              key={isDraft ? "draft" : `${index}-${shape.ordinal}`}
              className={`mask${!isDraft && selected === index ? " is-selected" : ""}`}
              style={{
                left: `${shape.left * 100}%`,
                top: `${shape.top * 100}%`,
                width: `${shape.width * 100}%`,
                height: `${shape.height * 100}%`,
                borderRadius: shape.shape === "ellipse" ? "50%" : undefined,
                opacity: isDraft ? 0.6 : 1,
              }}
              onPointerDown={(event) => {
                if (isDraft) return;
                event.stopPropagation();
                event.preventDefault();
                setSelected(index);
                const { x, y } = point(event);
                setDrag({ mode: "move", index, dx: x - shape.left, dy: y - shape.top });
              }}
              title={shape.label || `Region ${shape.ordinal}`}
            >
              {shape.ordinal}
              {!isDraft && selected === index && (
                <span
                  className="mask-handle"
                  onPointerDown={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    setDrag({ mode: "resize", index });
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="row row-wrap small">
        <span className="faint">Drag on the image to add a region. Click one to move or resize it.</span>
        <div className="grow" />
        <label className="checkline small">
          <input type="checkbox" checked={hideAll} onChange={toggleHideAll} disabled={!shapes.length} />
          <span>Hide all regions, reveal one</span>
        </label>
      </div>

      {shapes.length === 0 ? (
        <div className="small faint">
          No regions yet. This card can't be exported until it has at least one.
        </div>
      ) : (
        <div className="occlusion-list">
          {shapes.map((shape, index) => (
            <div
              key={index}
              className={`occlusion-row${selected === index ? " is-selected" : ""}`}
              onClick={() => setSelected(index)}
            >
              <span className="ord">{shape.ordinal}</span>
              <input
                type="text"
                className="grow"
                value={shape.label}
                placeholder="What is hidden here? (for your reference)"
                onChange={(e) => setLabel(index, e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
              <button
                className="btn-ghost btn-sm"
                onClick={(e) => { e.stopPropagation(); move(index, -1); }}
                disabled={index === 0}
                title="Move earlier"
              >
                ↑
              </button>
              <button
                className="btn-ghost btn-sm"
                onClick={(e) => { e.stopPropagation(); move(index, 1); }}
                disabled={index === shapes.length - 1}
                title="Move later"
              >
                ↓
              </button>
              <button
                className="btn-ghost btn-sm"
                onClick={(e) => { e.stopPropagation(); remove(index); }}
                title="Delete region"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="small faint">
        {shapes.length > 0 &&
          `Makes ${shapes.length} card${shapes.length === 1 ? "" : "s"} — one per region.`}
      </div>
    </div>
  );
}

function clamp(value: number, min = 0, max = 1): number {
  return Math.min(max, Math.max(min, value));
}
